// Multi-manager buyer teams — this team's advertisements (§3.2).
//
// 🔴 THE SAME TABLE AND THE SAME MODALS AS THE ADVERTISING PAGE, not a copy of
// them. `AdvertisementsTable`, `AdvertisementEditorModal` and
// `SpendLedgerModal` are the page's own components rendered here for one team,
// so a manager reading their team's tab and the global page cannot be shown
// two answers for one advertisement. Budgets and the cross-team roll-up stay
// on the global page, which this tab links across to.
//
// 🔴 SCOPE COMES FROM THE SERVER, exactly as it does on the page. Whether this
// caller may edit these advertisements is `canManageAds` from the scope
// endpoint AND the team's own `canManage` in that same payload — never
// re-derived here from a permission list. A caller who may not READ advertising
// at all is told so without a request that would 403.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone } from "lucide-react";
import type { AdvertisementDto } from "@/api/advertising";
import type { BuyerTeamDetailDto } from "@/api/buyerTeams";
import { Button, Card, EmptyState, Skeleton, StatCard, StatsBar, buttonVariants } from "@/design-system/components/ui";
import {
  useAdvertisements,
  useAdvertisingBudgets,
  useAdvertisingCostPerLead,
  useAdvertisingScope,
} from "@/hooks/buyers/useAdvertising";
import { useAuth } from "@/hooks/useAuth";
import { AdvertisementEditorModal } from "../advertising/AdvertisementEditorModal";
import { AdvertisementsTable } from "../advertising/AdvertisementsTable";
import { SpendLedgerModal } from "../advertising/SpendLedgerModal";
import { currentPeriod, money, OverBudgetBadge, periodLabel, utilizationLabel } from "../advertising/advertisingBits";
import { QueryErrorState } from "../queryStates";

/** The exact string from `Permissions.cs` — the read gate on every advertising
 *  endpoint, including the scope read this tab starts from. */
const READ_PERMISSION = "BuyerAdvertising.Read";

export function AdvertisingTab({ team }: { team: BuyerTeamDetailDto }) {
  const { hasPermission } = useAuth();
  const canRead = hasPermission(READ_PERMISSION);

  const scope = useAdvertisingScope(canRead);
  const scopeTeam = scope.data?.teams.find((t) => t.id === team.id) ?? null;
  // Fetched only once the scope says this team is one the caller reads — the
  // advertisements endpoint is team-scoped server-side, so asking for a team
  // outside the caller's scope is a request that would 403.
  const ads = useAdvertisements(scopeTeam ? team.id : null);

  const canManageThisTeam = scopeTeam?.canManage === true && scope.data?.canManageAds === true;

  // Budget pace for the CURRENT month — the budget from the team's history,
  // the spend from the month-bounded cost-per-lead report (`spendToDate` on
  // the rows is lifetime and would overstate every month after the first).
  const period = currentPeriod();
  const budgets = useAdvertisingBudgets(scopeTeam ? team.id : null);
  const pace = useAdvertisingCostPerLead(
    { from: period, to: period, buyerTeamId: team.id },
    { enabled: !!scopeTeam },
  );
  const budget = budgets.data?.find((b) => b.period === period) ?? null;
  const paceRow = pace.data?.budgets.find((b) => b.buyerTeamId === team.id) ?? null;
  const monthSpend = paceRow?.spend ?? null;
  const monthUsed =
    paceRow && paceRow.budget ? utilizationLabel(paceRow.spend / paceRow.budget) : null;

  const [editing, setEditing] = useState<AdvertisementDto | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [spendFor, setSpendFor] = useState<AdvertisementDto | null>(null);

  const pageHref = `/buyers/advertising?team=${encodeURIComponent(team.id)}`;

  if (!canRead) {
    return (
      <div data-testid="buyer-team-advertising-tab">
        <EmptyState
          icon={Megaphone}
          title="Advertising is a manager view"
          description={`Budgets, advertisements and spend for ${team.name} are read by buyer managers. Nothing here is a statement about what the team has spent.`}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-4" data-testid="buyer-team-advertising-tab">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-space-3 mb-space-3">
          <div className="min-w-0">
            <h2 className="font-display text-h3 text-onyx m-0">Advertisements</h2>
            <p className="text-caption text-gray-mid m-0 mt-space-1 max-w-reading">
              What each advertisement has cost and what it brought in. Reporting only — none of
              it moves money. Budgets and the monthly roll-up are on the advertising page.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-space-2">
            <Link
              to={pageHref}
              className={buttonVariants({ variant: "secondary", size: "sm" })}
              data-testid="buyer-team-advertising-link"
            >
              Open advertising
            </Link>
            {canManageThisTeam && (
              <Button
                size="sm"
                onClick={() => {
                  setEditing(null);
                  setEditorOpen(true);
                }}
                data-testid="buyer-team-advertising-new"
              >
                New advertisement
              </Button>
            )}
          </div>
        </div>

        {scopeTeam && (
          <div className="mb-space-4 space-y-space-2" data-testid="buyer-team-advertising-pace">
            <StatsBar>
              <StatCard
                label={`${periodLabel(period)} budget`}
                value={budget ? money(budget.amount) : "—"}
              />
              <StatCard
                label={`${periodLabel(period)} spend`}
                value={monthSpend === null ? "—" : money(monthSpend)}
              />
              <StatCard label={`${periodLabel(period)} used`} value={monthUsed ?? "—"} />
            </StatsBar>
            {/* A flag, never a block; nothing renders under budget. */}
            <OverBudgetBadge pace={paceRow} />
          </div>
        )}

        {scope.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : scope.isError ? (
          <QueryErrorState
            what="your advertising scope"
            onRetry={() => void scope.refetch()}
            retrying={scope.isFetching}
          />
        ) : !scopeTeam ? (
          /* The scope answered, and this team is not in it. A fact about the
             caller's scope, not about the team's advertising — said so rather
             than rendered as an empty table. */
          <EmptyState
            icon={Megaphone}
            title="Outside your advertising scope"
            description={`${team.name} is not one of the teams whose advertising you can read.`}
          />
        ) : ads.isError ? (
          <QueryErrorState
            what="this team's advertisements"
            onRetry={() => void ads.refetch()}
            retrying={ads.isFetching}
          />
        ) : ads.isLoading ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <AdvertisementsTable
            ads={ads.data ?? []}
            teamName={team.name}
            canManage={canManageThisTeam}
            onSpend={setSpendFor}
            onEdit={(a) => {
              setEditing(a);
              setEditorOpen(true);
            }}
          />
        )}
      </Card>

      {scopeTeam && (
        <AdvertisementEditorModal
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          buyerTeamId={team.id}
          advertisement={editing}
          brands={scope.data?.brands ?? []}
        />
      )}

      {spendFor && (
        <SpendLedgerModal
          open={spendFor !== null}
          onClose={() => setSpendFor(null)}
          advertisement={spendFor}
          canRecord={canManageThisTeam}
          canCorrect={scope.data?.canManageAllTeams === true}
        />
      )}
    </div>
  );
}
